import { DtsMailClient, FetchTransport, type MfaChallenge, type Session, type SessionStore } from '@dts/core';
import type { PublicSession } from './types';

const SESSION_KEY = 'dts.session';
const DEVICE_KEY = 'dts.deviceId';

function read<T>(key: string): T | null {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked (private mode): the session lasts for this tab only */
  }
}

/** Stable id for this browser; names its event queue and is sent as X-Device-Id. */
export function deviceId(): string {
  let id = read<string>(DEVICE_KEY);
  if (!id) {
    id = `web-${crypto.randomUUID()}`;
    write(DEVICE_KEY, id);
  }
  return id;
}

let cache: Session | null | undefined;

/**
 * The browser has no OS keychain, so tokens live in localStorage for this origin. Another tab signing in or out
 * updates this one through the storage event.
 */
export const sessionStore: SessionStore = {
  async load() {
    if (cache === undefined) cache = read<Session>(SESSION_KEY);
    return cache;
  },
  async save(s) {
    cache = s;
    write(SESSION_KEY, s);
  },
};

const expiredListeners = new Set<() => void>();
export function onSessionExpired(cb: () => void): () => void {
  expiredListeners.add(cb);
  return () => expiredListeners.delete(cb);
}

export const transport = new FetchTransport(sessionStore, {
  headers: { 'X-Device-Id': deviceId() },
  onSessionExpired: () => expiredListeners.forEach((cb) => cb()),
});

export const client = new DtsMailClient(transport);

window.addEventListener('storage', (e) => {
  if (e.key !== SESSION_KEY) return;
  const had = !!cache;
  cache = e.newValue ? (JSON.parse(e.newValue) as Session) : null;
  if (had && !cache) expiredListeners.forEach((cb) => cb());
});

export async function publicSession(): Promise<PublicSession | null> {
  const s = await sessionStore.load();
  return s ? { baseUrl: s.baseUrl, user: s.user } : null;
}

const clean = (baseUrl: string) => baseUrl.trim().replace(/\/+$/, '');

/** Password step only; the session comes from verifyMfa / enableMfa. */
export function login(baseUrl: string, email: string, password: string): Promise<MfaChallenge> {
  return FetchTransport.login(clean(baseUrl), email.trim(), password);
}

export function acceptInvite(baseUrl: string, email: string, code: string, password: string): Promise<MfaChallenge> {
  return FetchTransport.acceptInvite(clean(baseUrl), email.trim(), code.trim(), password);
}

export async function verifyMfa(baseUrl: string, mfaToken: string, code: string): Promise<PublicSession> {
  const s = await FetchTransport.verifyMfa(clean(baseUrl), mfaToken, code.trim());
  await sessionStore.save(s);
  return { baseUrl: s.baseUrl, user: s.user };
}

export async function enableMfa(baseUrl: string, mfaToken: string, code: string): Promise<{ session: PublicSession; recoveryCodes: string[] }> {
  const r = await FetchTransport.enableMfa(clean(baseUrl), mfaToken, code.trim());
  await sessionStore.save(r.session);
  return { session: { baseUrl: r.session.baseUrl, user: r.session.user }, recoveryCodes: r.recoveryCodes };
}

export async function logout(): Promise<void> {
  await transport.logout();
}
